import { isCheckoutHeldOrderState, type Order } from '@order-up/models';

/** The order a cart already became — where the checkout sends the guest instead of charging. */
export interface PlacedCartOrder {
    readonly orderId: number;
    /** Absent only on a malformed order; the page then shows an error, never a blank route. */
    readonly orderGuid: string | undefined;
}

/**
 * The checkout's duplicate-payment check, asked before a submit charges
 * anything: has this cart ALREADY been placed as an order?
 *
 * An order exists at a cart only once it has been paid for. It is written after
 * an approved charge, or — online persist-first — written beforehand HELD for
 * checkout (`awaitingPayment` / `abandoned`, {@link isCheckoutHeldOrderState})
 * and released by the approved charge. So the cart is placed exactly when its
 * order is not checkout-held. A held order is the one this submit is about to
 * pay for, never a reason to stop it.
 *
 * `order` is what `ordersApi.getOrderByCartId` returns: the `Order` model
 * (`Order.fromJSON`). The check used to read `paymentStatus === 'paid'`, a field
 * that model does not carry, so it never fired — a guest whose first payment's
 * answer was lost re-submitted, and the server's one-cart-one-order guard
 * answered 409 CART_ALREADY_CONVERTED where the page should have taken them to
 * their order.
 */
export function placedOrderForCart(
    order: Pick<Order, 'orderId' | 'guid' | 'orderState'> | null | undefined,
): PlacedCartOrder | null {
    if (!order || isCheckoutHeldOrderState(order.orderState)) return null;
    return { orderId: order.orderId, orderGuid: order.guid || undefined };
}
